/**
 * One invoice — Phase 21, Task 21.11. PRD 10.4, 10.8, 10.10, 10.12.
 *
 * THE RULE THAT SHAPES THIS FILE: only a DRAFT may be edited or discarded.
 * Once an invoice has been issued it is a legal document, and the backend
 * refuses every change to it (409 `error.invoices.immutable`,
 * `error.invoices.delete_not_allowed`). So this page does not render an edit
 * or delete affordance on a non-draft AT ALL — not disabled, not behind a
 * tooltip, absent. If the backend would refuse an action, it is not offered
 * (Principle 2). Corrections are made with a credit note (Task 21.13).
 *
 * NO MONEY IS COMPUTED HERE. Line totals, `vat_amount`, `line_total_incl_vat`,
 * the VAT bands, öresavrundning and the total are all produced by the Decimal
 * engine in `backend/services/invoicing.py` and re-produced on every draft
 * save. While a draft has unsaved edits the totals shown are the last SAVED
 * ones, and the page says so rather than guessing at new ones.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    getInvoice,
    updateInvoice,
    deleteInvoice,
    issueInvoice,
    markInvoiceSent,
    downloadInvoicePdf,
    sendInvoice,
    uploadInvoiceAttachment,
    deleteInvoiceAttachment,
    downloadInvoiceAttachment,
    registerInvoicePayment,
    voidInvoicePayment,
    creditInvoice,
    pricePreviewInvoice,
} from '../../lib/api';
import { getApiErrorMessage } from '../../lib/errors';
import { formatDate } from '../../lib/format-utils';
import {
    canCreditInvoice,
    canRegisterPayment,
    currencySuffix,
    deriveInvoiceState,
    downloadBlobResponse,
    formatMoney,
    formatPercent,
    invoiceStateLabelKey,
    invoiceStateVariant,
    isPaymentVoided,
    PAYMENT_METHODS,
    paymentMethodLabel,
    previewLineMoney,
    unitLabel,
} from './invoice-utils';
import InvoiceA4Sheet from '../../components/invoice-a4-sheet';
import InvoicePdfFrame from '../../components/invoice-pdf-frame';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    ArrowLeft, Download, Loader2, Plus, Trash2, ChevronUp, ChevronDown, Mail, Lock,
    Ban, AlertTriangle, Undo2, FileText, Rows3, EyeOff, CheckCircle2, X, Paperclip,
} from 'lucide-react';

// The date a payment defaults to: today in the VIEWER's timezone. UTC would
// date a payment received on the 1st in Stockholm as the 31st of the month
// before, which lands it in the wrong month of a bookkeeping year.
function todayField() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

// Sentinel for "no method chosen". A radix Select item cannot carry an empty
// value, and `method` must go to the backend as null rather than "".
const NO_METHOD = '__none__';

// models/invoice.py InvoicePaymentRequest field bounds. Enforced here only so
// the input stops before the backend has to reject the write.
const PAYMENT_REFERENCE_MAX = 100;
const PAYMENT_NOTE_MAX = 1000;
const VOID_REASON_MAX = 500;

// Mirrors models/invoice.py KNOWN_UNITS. `unit` is not a Literal on the
// backend — a tenant's own free-text unit is legitimate — so an unrecognised
// value already on a line is added to the options rather than silently
// replaced by one of these.
const KNOWN_UNITS = ['hour', 'day', 'week', 'month', 'piece', 'km', 'mil', 'percent', 'fixed'];

// Same set as ClientsPage / SupplierInvoicesPage.
const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP'];

// models/invoice.py TEXT_BLOCK_MAX_LENGTH. Enforced here only so the input
// stops before the backend has to reject the save — the backend remains the
// authority on the limit.
const TEXT_BLOCK_MAX_LENGTH = 4000;

/**
 * The four admin-authored free-text blocks (PRD 10.14), IN THE ORDER THE PDF
 * PRINTS THEM (`services/invoice_pdf.py` CUSTOM_TEXT_FIELDS):
 *
 *   meta columns → header_note → intro_text → LINE TABLE → notes → totals
 *   → footer_note → the company footer line
 *
 * The order is the whole reason there are four fields rather than one, so the
 * screen lists them in it and numbers them to match.
 */
const TEXT_BLOCKS = [
    { field: 'header_note', rows: 3 },
    { field: 'intro_text', rows: 4 },
    { field: 'notes', rows: 4 },
    { field: 'footer_note', rows: 3 },
];

/**
 * Parse a number a Swedish user typed. "1 234,50" and "1234.50" both mean the
 * same figure, and a decimal comma is what a Swedish keyboard produces.
 *
 * Returns null for anything that is not a finite number — including the empty
 * string. An empty numeric field must NEVER be sent to the backend, which
 * types quantity, price and VAT rate: the caller turns a null into a
 * field-level error instead.
 */
function parseDecimal(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    if (cleaned === '') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
}

/** Numbers into form state as strings — never as `null`, which React warns on. */
function toField(value) {
    return value === null || value === undefined ? '' : String(value);
}

function emptyLine() {
    return {
        designation: '',
        quantity: '1',
        unit: 'piece',
        price_per_unit: '',
        vat_rate: '25',
        project_id: null,
        source: null,
    };
}

function lineToForm(line) {
    return {
        designation: line.designation || '',
        quantity: toField(line.quantity),
        unit: line.unit || '',
        price_per_unit: toField(line.price_per_unit),
        vat_rate: toField(line.vat_rate),
        project_id: line.project_id ?? null,
        // Carried through untouched: `source` is the double-billing guard's
        // handle on the time entries and expenses this line billed (PRD 10.7).
        // Dropping it on an edit would let the same work be billed twice.
        source: line.source ?? null,
    };
}

function invoiceToForm(invoice) {
    return {
        invoice_date: invoice.invoice_date || '',
        due_date: invoice.due_date || '',
        delivery_date: invoice.delivery_date || '',
        our_reference: invoice.our_reference || '',
        their_reference: invoice.their_reference || '',
        your_order_no: invoice.your_order_no || '',
        reference_no: invoice.reference_no || '',
        currency: invoice.currency || 'SEK',
        // The four text blocks (PRD 10.14). The API types all four as
        // `str = ""`, so an unset block is already "" and never null.
        header_note: invoice.header_note || '',
        intro_text: invoice.intro_text || '',
        notes: invoice.notes || '',
        footer_note: invoice.footer_note || '',
    };
}

/**
 * The narrowest viewport the A4 draft surface is offered on.
 *
 * A4 is a FIXED PORTRAIT SHAPE and does not survive a phone screen: shrinking
 * a 210 mm page into a 390 px column produces something nobody can read or
 * type into. Below this width the stacked form is not a fallback so much as
 * the right surface, and it is the only one offered — the toggle is not
 * rendered at all, rather than rendered and disabled.
 *
 * 1024 px rather than a tablet-portrait 768: the dashboard keeps a fixed
 * sidebar at these widths, so the content column is a few hundred pixels
 * narrower than the viewport, and 768 px of viewport leaves no room for a page.
 */
const A4_MIN_VIEWPORT = '(min-width: 1024px)';

function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => (
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(query).matches
            : false
    ));

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }
        const mql = window.matchMedia(query);
        const onChange = (event) => setMatches(event.matches);
        setMatches(mql.matches);
        // `addListener` is the Safari < 14 spelling; both are kept so a rotate
        // or resize cannot strand the page in the wrong surface.
        if (mql.addEventListener) {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
}

/**
 * The internal facts that travel WITH the invoice but never appear ON it
 * (PRD 11.6, Task 22.4).
 *
 * >>> THE COST CENTRE MUST NEVER REACH THE CUSTOMER-FACING DOCUMENT. <<<
 * It is Fortnox posting data — which of the tenant's service lines the revenue
 * books against — and `invoice_spec.py` and `invoice_pdf.py` deliberately do
 * not read it. So it lives here, in the app's chrome, and never inside the A4
 * sheet.
 *
 * Two shapes, because the truth has two shapes:
 *
 *   - Every line resolving to the SAME cost centre is the ordinary case, and
 *     it is ONE fact about the invoice — shown once.
 *   - Lines genuinely DIFFERING is legitimate (the tenant's cost codes are
 *     service lines, not customer buckets, so a customer buying two services
 *     lands in two centres). It is shown PER LINE. Collapsing it to whichever
 *     one came first would misfile revenue; hiding the difference would file
 *     it silently.
 *
 * A line with no cost centre — a manual or fixed-price line, or a project with
 * no code — is simply BLANK. It is not "unknown" and it is not an error.
 *
 * Everything read here comes off `invoice.lines`, which is the SERVER-RESOLVED
 * copy. The draft's in-progress form lines carry no cost centre at all: it is
 * resolved from the project on save and a caller cannot supply one.
 */
function InvoiceInternalPanel({ invoice }) {
    const { t } = useTranslation();
    const lines = invoice.lines || [];
    if (lines.length === 0) return null;

    // 23.R5: `null` stands in for "no cost centre" as its OWN distinct value —
    // dropping it here (the previous `.filter(Boolean)`) let one line on cost
    // centre X and one line with none collapse to `distinct = [X]`, reporting
    // a false uniform "X" and hiding that a line is actually unassigned. This
    // is internal Fortnox posting data (PRD 11.6) where per-line accuracy is
    // the point, so a mix of named and blank must fall through to the per-line
    // table below. All-blank still counts as one distinct value (`null`), so
    // that genuinely uniform case still collapses to a single "—" row.
    const distinct = [...new Set(lines.map((line) => line.cost_code_id || null))];
    const anySource = lines.some((line) => line.source);
    const named = lines.find((line) => line.cost_code_id)?.cost_code_name || '';
    const uniform = distinct.length <= 1;

    return (
        <Card className="border border-dashed border-border shadow-none" data-testid="invoice-internal-panel">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <EyeOff className="w-4 h-4 text-muted-foreground shrink-0" />
                    <h2 className="label-uppercase text-muted-foreground">
                        {t('invoices.internal_title')}
                    </h2>
                </div>

                {uniform && !anySource ? (
                    <div className="flex justify-between gap-4 text-sm">
                        <span className="text-muted-foreground">{t('invoices.internal_cost_centre')}</span>
                        <span data-testid="internal-cost-centre">{named || '—'}</span>
                    </div>
                ) : (
                    <>
                        {!uniform && (
                            <p className="text-xs text-muted-foreground" data-testid="internal-cost-centre-mixed">
                                {t('invoices.internal_cost_centre_mixed')}
                            </p>
                        )}
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{t('invoices.internal_col_line')}</th>
                                        <th>{t('invoices.internal_col_cost_centre')}</th>
                                        <th>{t('invoices.internal_col_source')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((line, index) => (
                                        <tr key={index} data-testid={`internal-line-${index}`}>
                                            <td>{line.designation}</td>
                                            <td data-testid={`internal-cost-centre-${index}`}>
                                                {line.cost_code_name || '—'}
                                            </td>
                                            <td className="text-muted-foreground text-sm">
                                                {line.source
                                                    ? t(`invoices.source_${line.source.source_type}`, {
                                                        count: line.source.source_ids?.length || 0,
                                                    })
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

/** A label/value pair in the document's meta grid. */
function MetaRow({ label, value }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex justify-between gap-4 py-1 border-b border-border/50 last:border-0">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className="text-sm text-right">{value}</span>
        </div>
    );
}

function AddressBlock({ title, lines }) {
    const visible = (lines || []).filter(Boolean);
    if (visible.length === 0) return null;
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                {title}
            </p>
            {visible.map((line, index) => (
                <p key={index} className="text-sm">{line}</p>
            ))}
        </div>
    );
}

export default function InvoiceDetailPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [form, setForm] = useState(null);
    const [lines, setLines] = useState([]);
    const [lineErrors, setLineErrors] = useState({});
    const [dirty, setDirty] = useState(false);

    const [saving, setSaving] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const [issueOpen, setIssueOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [creditOpen, setCreditOpen] = useState(false);
    // The payment row a void is being confirmed for, or null.
    const [voidTarget, setVoidTarget] = useState(null);

    /**
     * A credit outcome that the admin has to READ, kept on the page rather
     * than only in a toast — a toast that scrolls past is a warning nobody
     * acted on, and both of these say something about money that will
     * otherwise go quietly wrong.
     *
     *   'reversal_incomplete' — the credit note is issued and legally valid,
     *     but some of the work the original billed could not be returned to
     *     the billable pool and will therefore never be re-billed.
     *   'issue_failed' — a PARTIAL SUCCESS. The original IS credited and IS
     *     linked to a credit note, but that credit note is still a draft and
     *     has to be issued by hand. Crediting again is exactly the wrong move.
     */
    const [creditNotice, setCreditNotice] = useState(null);

    /**
     * Which surface a DRAFT is edited on (KD-081d).
     *
     * `draftView` is what the admin asked for; `showSheet` is what they get,
     * because the A4 surface needs the width. An ISSUED invoice has no choice
     * to make — it is always the archived PDF.
     */
    const wideEnoughForSheet = useMediaQuery(A4_MIN_VIEWPORT);
    const [draftView, setDraftView] = useState('document');

    const applyInvoice = useCallback((doc) => {
        setInvoice(doc);
        setForm(invoiceToForm(doc));
        setLines((doc.lines || []).map(lineToForm));
        setLineErrors({});
        setDirty(false);
    }, []);

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getInvoice(id);
            applyInvoice(res.data);
            setLoadError(null);
        } catch (error) {
            setInvoice(null);
            setLoadError(getApiErrorMessage(error, t('invoices.failed_load')));
        } finally {
            setLoading(false);
        }
    }, [id, applyInvoice, t]);

    useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

    const isDraft = invoice?.status === 'draft';
    const isCreditNote = invoice?.doc_type === 'credit_note';
    const isIssuedUnsent = !isCreditNote && invoice?.status === 'sent' && !invoice?.sent_at && (!invoice?.payments || invoice.payments.length === 0);
    const canMarkSent = !isDraft && !invoice?.sent_at;
    const isEditable = isDraft || isIssuedUnsent;
    const [markingSent, setMarkingSent] = useState(false);
    const [markSentOpen, setMarkSentOpen] = useState(false);

    const handleOpenSend = () => {
        if (dirty) {
            toast.warning(t('invoices.issue_unsaved_warning'));
            return;
        }
        setSendOpen(true);
    };

    const handleOpenMarkSent = () => {
        if (dirty) {
            toast.warning(t('invoices.issue_unsaved_warning'));
            return;
        }
        setMarkSentOpen(true);
    };

    const handleMarkSent = async () => {
        setMarkingSent(true);
        try {
            const res = await markInvoiceSent(invoice.id);
            applyInvoice(res.data);
            setMarkSentOpen(false);
            toast.success(t('invoices.marked_as_sent_toast', { number: invoice.invoice_number || '' }));
        } catch (err) {
            toast.error(getApiErrorMessage(err, t('invoices.failed_mark_sent')));
        } finally {
            setMarkingSent(false);
        }
    };
    const state = useMemo(() => (invoice ? deriveInvoiceState(invoice) : null), [invoice]);

    // Both mirror the backend's own refusals (see invoice-utils). An action
    // the API would refuse is not rendered at all — not disabled, absent.
    const payable = canRegisterPayment(invoice);
    const creditable = canCreditInvoice(invoice);

    const client = invoice?.client_snapshot || {};
    // `company_snapshot` is frozen at ISSUE and empty on a draft by design.
    // The stacked-form fallback mirrors the A4 sheet's own fix (Task 23.9,
    // PRD 12.9): on a draft, prefer `company_preview` — the seller block as
    // it would resolve RIGHT NOW — so the bank details this card shows and
    // the ones the sheet shows are never two different answers to the same
    // question. Never used on an issued invoice: `company_preview` is only
    // ever present while `status === "draft"`.
    const company = (isDraft && invoice?.company_preview) || invoice?.company_snapshot || {};

    const setFormField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setDirty(true);
    };

    const setLineField = (index, field, value) => {
        setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
        setLineErrors((prev) => {
            if (!prev[index]?.[field]) return prev;
            const next = { ...prev, [index]: { ...prev[index] } };
            delete next[index][field];
            return next;
        });
        setDirty(true);
    };

    const addLine = () => {
        setLines((prev) => [...prev, emptyLine()]);
        setDirty(true);
    };

    const removeLine = (index) => {
        setLines((prev) => prev.filter((_, i) => i !== index));
        setLineErrors({});
        setDirty(true);
    };

    const moveLine = (index, direction) => {
        const target = index + direction;
        setLines((prev) => {
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
        setLineErrors({});
        setDirty(true);
    };

    /**
     * Validate the lines and turn them into the backend's shape.
     *
     * Every numeric field is parsed here and a blank or unparseable one becomes
     * a FIELD-LEVEL error rather than an empty string on the wire: the backend
     * types quantity, price and VAT rate, so `""` is a 422 the user cannot make
     * sense of. Nothing is silently defaulted — this is money on a document
     * somebody will be asked to pay.
     */
    const buildLinePayload = () => {
        const errors = {};
        const payload = [];

        lines.forEach((line, index) => {
            const rowErrors = {};

            const designation = (line.designation || '').trim();
            if (!designation) rowErrors.designation = t('invoices.line_designation_required');

            const quantity = parseDecimal(line.quantity);
            if (quantity === null) rowErrors.quantity = t('invoices.invalid_quantity');

            const price = parseDecimal(line.price_per_unit);
            if (price === null) rowErrors.price_per_unit = t('invoices.invalid_price');

            const vatRate = parseDecimal(line.vat_rate);
            if (vatRate === null || vatRate < 0 || vatRate > 100) {
                rowErrors.vat_rate = t('invoices.invalid_vat_rate');
            }

            if (Object.keys(rowErrors).length > 0) {
                errors[index] = rowErrors;
                return;
            }

            payload.push({
                designation,
                quantity,
                unit: (line.unit || '').trim(),
                price_per_unit: price,
                vat_rate: vatRate,
                project_id: line.project_id ?? null,
                source: line.source ?? null,
            });
        });

        setLineErrors(errors);
        return Object.keys(errors).length === 0 ? payload : null;
    };

    /**
     * The same parse `buildLinePayload` runs, WITHOUT its side effect.
     *
     * `buildLinePayload` calls `setLineErrors` unconditionally, which is
     * right for a Save click but wrong for something firing on every
     * keystroke — it would paint field-level errors while the admin is
     * mid-type. This is the read-only half: it returns the identical wire
     * shape, or null the moment any line fails the identical checks, so a
     * preview request is only ever sent for lines a save would accept.
     */
    const buildPreviewLinePayload = () => {
        const payload = [];
        for (const line of lines) {
            const designation = (line.designation || '').trim();
            if (!designation) return null;

            const quantity = parseDecimal(line.quantity);
            if (quantity === null) return null;

            const price = parseDecimal(line.price_per_unit);
            if (price === null) return null;

            const vatRate = parseDecimal(line.vat_rate);
            if (vatRate === null || vatRate < 0 || vatRate > 100) return null;

            payload.push({
                designation,
                quantity,
                unit: (line.unit || '').trim(),
                price_per_unit: price,
                vat_rate: vatRate,
                project_id: line.project_id ?? null,
                source: line.source ?? null,
            });
        }
        return payload;
    };

    /**
     * Live draft totals (Task 23.9, PRD 12.8) — the server's REAL engine
     * prices the unsaved lines, replacing the "these are last save's figures"
     * notice with a genuine preview.
     *
     * `status`: 'idle' (clean draft — nothing to preview, the saved totals
     * are shown as-is) | 'loading' (a request is in flight; `data` keeps
     * whatever the last successful preview was, so typing a second keystroke
     * never blanks a figure that was already on screen) | 'success' | 'error'
     * (the lines do not parse locally, or the request failed — `data` is
     * cleared so the sheet falls back to the SAME saved-figures-plus-notice
     * behaviour Task 22.5 shipped, rather than showing a stale or invented
     * number).
     */
    const [totalsPreview, setTotalsPreview] = useState({ status: 'idle', data: null });

    // A flattened fingerprint of every line's priced fields — extracted to
    // its own variable (rather than inline in the effect's deps array below)
    // because an inline `.map().join()` there is flagged as a "complex
    // expression" eslint cannot statically check, even disabled. Recomputed
    // every render, which is fine: it is cheap and only ever compared by
    // value as a dependency.
    const linesSignature = lines
        .map((l) => `${l.designation}|${l.quantity}|${l.unit}|${l.price_per_unit}|${l.vat_rate}|${l.project_id ?? ''}|${l.source ?? ''}`)
        .join('~');

    // Debounced, mirroring the FX-preview pattern in day-receipt-form.jsx: a
    // `cancelled` flag guards both the pending timer and the in-flight
    // request, so a response that is no longer the latest one can never
    // overwrite whatever arrived after it.
    useEffect(() => {
        if (!isEditable || !dirty || !invoice?.id) {
            setTotalsPreview({ status: 'idle', data: null });
            return undefined;
        }

        const payload = buildPreviewLinePayload();
        if (payload === null) {
            // Not something the server would accept either — fall back
            // instead of asking it, exactly as a failed request would.
            setTotalsPreview({ status: 'error', data: null });
            return undefined;
        }

        let cancelled = false;
        setTotalsPreview((prev) => ({ status: 'loading', data: prev.data }));

        const timer = setTimeout(async () => {
            if (cancelled) return;
            try {
                const res = await pricePreviewInvoice(invoice.id, {
                    lines: payload,
                    currency: form?.currency || invoice.currency || undefined,
                });
                if (cancelled) return;
                setTotalsPreview({ status: 'success', data: res.data });
            } catch {
                if (cancelled) return;
                setTotalsPreview({ status: 'error', data: null });
            }
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // Deps are `linesSignature` (not `lines` itself, whose array identity
        // changes on every keystroke anyway) plus the handful of other
        // fields the preview body carries. Intentionally excludes
        // `buildPreviewLinePayload`, which closes over `lines` and is
        // recreated every render for the same reason `lines` itself is not a
        // useful dependency here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditable, dirty, invoice?.id, invoice?.currency, form?.currency, linesSignature]);

    const handleSave = async () => {
        const linePayload = buildLinePayload();
        if (linePayload === null) {
            toast.error(t('invoices.fix_line_errors'));
            return;
        }

        setSaving(true);
        try {
            const blankToNull = (value) => (value && value.trim() ? value.trim() : null);
            const res = await updateInvoice(invoice.id, {
                invoice_date: blankToNull(form.invoice_date),
                due_date: blankToNull(form.due_date),
                delivery_date: blankToNull(form.delivery_date),
                our_reference: blankToNull(form.our_reference),
                their_reference: blankToNull(form.their_reference),
                your_order_no: blankToNull(form.your_order_no),
                reference_no: blankToNull(form.reference_no),
                currency: form.currency || 'SEK',
                // All four text blocks are sent on every draft save, always as
                // a string. An emptied block is a deliberate clear and must go
                // as "" — omitting the key would leave the old text in place
                // and the admin would watch their deletion undo itself.
                header_note: form.header_note || '',
                intro_text: form.intro_text || '',
                notes: form.notes || '',
                footer_note: form.footer_note || '',
                lines: linePayload,
            });
            applyInvoice(res.data);
            toast.success(invoice?.status === 'draft' ? t('invoices.draft_saved') : t('common.saved'));
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_save')));
        } finally {
            setSaving(false);
        }
    };

    const handleIssue = async () => {
        setIssuing(true);
        try {
            const res = await issueInvoice(invoice.id);
            applyInvoice(res.data);
            setIssueOpen(false);
            toast.success(t('invoices.issued_toast', { number: res.data.invoice_number }));
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_issue')));
        } finally {
            setIssuing(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteInvoice(invoice.id);
            toast.success(t('invoices.draft_deleted'));
            navigate('/dashboard/invoices');
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_delete')));
            setDeleting(false);
        }
    };

    /**
     * Apply the outcome of a successful credit.
     *
     * `original` is the already re-read document with `status: "credited"` and
     * `credited_by_invoice_id` on it, so applying it verbatim is the whole
     * update and a re-fetch could only show a staler copy.
     *
     * `sources_reconciled: false` is NOT an error — the credit note is issued
     * and legally valid — but it means rows the original billed could not be
     * returned to the billable pool. That work will not be offered by the next
     * generation and will silently go unbilled unless somebody is told, so it
     * is raised to a notice that stays on the page.
     */
    const handleCredited = (data) => {
        applyInvoice(data.original);
        setCreditOpen(false);

        if (data.sources_reconciled === false) {
            setCreditNotice({
                kind: 'reversal_incomplete',
                conflicts: Object.keys(data.source_conflicts || {}).length,
                missing: (data.source_missing || []).length,
                failed: (data.source_failed || []).length,
            });
        } else {
            setCreditNotice(null);
        }

        toast.success(
            t('invoices.credited_toast', {
                number: data.credit_note?.invoice_number || '',
            }),
            { description: t('invoices.credit_sources_returned', { rows: data.sources_reversed || 0 }) }
        );
    };

    /**
     * `credit_note_issue_failed` is a PARTIAL SUCCESS dressed as a 500: the
     * original is already marked credited and already linked to a credit note
     * that is still a draft. Re-reading it is what makes that link render, so
     * the repair path — open the credit note and issue it — stays reachable
     * instead of leaving the admin on a page that shows no correction at all.
     */
    const handleCreditIssueFailed = async () => {
        setCreditOpen(false);
        setCreditNotice({ kind: 'issue_failed' });
        await fetchInvoice();
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await downloadInvoicePdf(invoice.id);
            downloadBlobResponse(response, `${invoice.invoice_number || 'draft'}.pdf`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_download')));
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="space-y-4" data-testid="invoice-not-found">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/invoices')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('invoices.back_to_list')}
                </Button>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-8 text-center text-muted-foreground">
                        {loadError || t('error.invoices.not_found')}
                    </CardContent>
                </Card>
            </div>
        );
    }

    const currency = isEditable ? (form?.currency || invoice.currency) : invoice.currency;
    const title = invoice.doc_type === 'credit_note'
        ? t('invoices.doc_type_credit_note')
        : t('invoices.doc_type_invoice');

    // The units offered: the known keys, plus whatever this document already
    // carries, so a tenant's own free-text unit survives an edit.
    const unitOptions = Array.from(new Set([
        ...KNOWN_UNITS,
        ...lines.map((line) => (line.unit || '').trim()).filter(Boolean),
    ]));

    // The A4 surface is an editable affordance and a wide-screen one. Everything
    // else falls back to the stacked form, which stays the primary surface on
    // a phone and is never taken away.
    const showSheet = isEditable && wideEnoughForSheet && draftView === 'document';
    const showForm = isEditable && !showSheet;

    // The `Moms kr` column header is a factual claim about the figures beneath
    // it — see `currencySuffix`. Kept identical in both draft surfaces.
    const suffix = currencySuffix(currency);
    const vatAmountHeader = suffix
        ? t('invoices.sheet_col_vat_amount', { currency: suffix })
        : t('invoices.sheet_col_vat_amount_plain');

    return (
        <div className="space-y-6" data-testid="invoice-detail-page">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/invoices')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('invoices.back_to_list')}
            </Button>

            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="font-heading text-4xl font-bold tracking-tight">
                            {title} {invoice.invoice_number || ''}
                        </h1>
                        <Badge variant={invoiceStateVariant(state)} data-testid="invoice-state-badge">
                            {t(invoiceStateLabelKey(state))}
                        </Badge>
                    </div>
                    <p className="text-muted-foreground mt-1">
                        {client.name || '—'}
                        {!invoice.invoice_number && ` · ${t('invoices.no_number_yet')}`}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    {/* Which surface the DRAFT is edited on. Rendered only when
                        there is genuinely a choice — on a narrow screen the A4
                        page is not offered at all rather than offered and
                        broken, and an issued invoice has no choice to make. */}
                    {isEditable && wideEnoughForSheet && (
                        <div
                            className="inline-flex border border-border"
                            role="group"
                            aria-label={t('invoices.view_label')}
                            data-testid="invoice-view-toggle"
                        >
                            <Button
                                variant={draftView === 'document' ? 'default' : 'ghost'}
                                size="sm"
                                className="rounded-none"
                                aria-pressed={draftView === 'document'}
                                onClick={() => setDraftView('document')}
                                data-testid="invoice-view-document"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                {t('invoices.view_document')}
                            </Button>
                            <Button
                                variant={draftView === 'form' ? 'default' : 'ghost'}
                                size="sm"
                                className="rounded-none"
                                aria-pressed={draftView === 'form'}
                                onClick={() => setDraftView('form')}
                                data-testid="invoice-view-form"
                            >
                                <Rows3 className="w-4 h-4 mr-2" />
                                {t('invoices.view_form')}
                            </Button>
                        </div>
                    )}

                    <Button
                        variant="outline"
                        onClick={handleDownload}
                        disabled={downloading}
                        data-testid="download-invoice-btn"
                    >
                        {downloading
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Download className="w-4 h-4 mr-2" />}
                        {t('invoices.download_pdf')}
                    </Button>

                    {/* An issued document can be emailed; a draft cannot — there
                        is nothing legal to send yet. */}
                    {!isDraft && (
                        <Button variant="outline" onClick={handleOpenSend} data-testid="send-invoice-btn">
                            <Mail className="w-4 h-4 mr-2" />
                            {t('invoices.send')}
                        </Button>
                    )}

                    {canMarkSent && (
                        <Button
                            variant="outline"
                            onClick={handleOpenMarkSent}
                            disabled={markingSent}
                            data-testid="mark-sent-invoice-btn"
                        >
                            {markingSent ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                            )}
                            {t('invoices.mark_as_sent')}
                        </Button>
                    )}

                    {/* Crediting is the ONLY correction an issued invoice
                        takes, and it is irreversible — so it lives here beside
                        the other document-level actions, and behind a
                        confirmation that spells out what it does. */}
                    {creditable && (
                        <Button
                            variant="outline"
                            onClick={() => setCreditOpen(true)}
                            data-testid="credit-invoice-btn"
                        >
                            <Undo2 className="w-4 h-4 mr-2" />
                            {t('invoices.credit_invoice')}
                        </Button>
                    )}

                    {/* Edit/discard/issue exist ONLY on a draft (PRD 10.8). */}
                    {isDraft && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => setDeleteOpen(true)}
                                data-testid="discard-draft-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('invoices.discard_draft')}
                            </Button>
                            <Button onClick={() => setIssueOpen(true)} data-testid="issue-invoice-btn">
                                {t('invoices.issue')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* What a credit actually did — kept on the page, not only in a
                toast, because both of these cost money if they go unread. */}
            {creditNotice && (
                <div
                    className="border border-destructive p-3 flex items-start gap-2 text-sm"
                    data-testid="credit-notice"
                >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
                    <div className="space-y-1">
                        {creditNotice.kind === 'issue_failed' ? (
                            <p>{t('error.invoices.credit_note_issue_failed')}</p>
                        ) : (
                            <>
                                <p className="font-medium">{t('invoices.credit_sources_warning_title')}</p>
                                <p>
                                    {t('invoices.credit_sources_warning_body', {
                                        conflicts: creditNotice.conflicts,
                                        missing: creditNotice.missing,
                                        failed: creditNotice.failed,
                                    })}
                                </p>
                            </>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="px-0"
                            onClick={() => setCreditNotice(null)}
                            data-testid="dismiss-credit-notice"
                        >
                            {t('common.close')}
                        </Button>
                    </div>
                </div>
            )}

            {/* Honest state notices */}
            {!isEditable && (
                <div className="border border-border p-3 flex items-start gap-2 text-sm" data-testid="immutable-notice">
                    <Lock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div>
                        <p>{t('invoices.immutable_notice')}</p>
                        {invoice.sent_at ? (
                            <p className="text-muted-foreground mt-1" data-testid="sent-notice">
                                {t('invoices.sent_notice', {
                                    date: formatDate(invoice.sent_at),
                                    recipients: (invoice.sent_to || []).join(', ') || '—',
                                })}
                            </p>
                        ) : (
                            <p className="text-muted-foreground mt-1" data-testid="not-emailed-notice">
                                {t('invoices.not_emailed_notice')}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Internal-only facts. Never inside the A4 sheet — see the
                component's own docstring. */}
            <InvoiceInternalPanel invoice={invoice} />

            {/* Credit-note cross references (Task 21.13 issues them; the links
                work the moment one exists). These are APP facts — the uuid link
                between two documents — and belong to the chrome in both
                surfaces, so they sit here rather than inside a details card
                that only one of the two renders. */}
            {(invoice.credit_of_invoice_id || invoice.credited_by_invoice_id) && (
                <div className="space-y-1" data-testid="invoice-credit-links">
                    {invoice.credit_of_invoice_id && (
                        <p className="text-sm">
                            {t('invoices.meta_credit_of')}{' '}
                            <Link
                                className="underline underline-offset-2"
                                to={`/dashboard/invoices/${invoice.credit_of_invoice_id}`}
                            >
                                {t('invoices.open_invoice')}
                            </Link>
                        </p>
                    )}
                    {invoice.credited_by_invoice_id && (
                        <p className="text-sm">
                            {t('invoices.meta_credited_by')}{' '}
                            <Link
                                className="underline underline-offset-2"
                                to={`/dashboard/invoices/${invoice.credited_by_invoice_id}`}
                            >
                                {t('invoices.open_invoice')}
                            </Link>
                        </p>
                    )}
                </div>
            )}

            {/*
                ═══ THE DOCUMENT ═══

                KD-081d. A DRAFT is edited on an A4-shaped surface (or the
                stacked form, on a narrow screen or on request). Anything that
                is no longer a draft shows the REAL ARCHIVED PDF — never an HTML
                replica, which would be a second renderer able to disagree with
                the document the customer actually receives.
            */}
            {!isEditable && (
                <>
                    {/* App-level facts about the document that are deliberately
                        NOT printed on it: the VAT treatment (the renderer emits
                        no statutory wording at all) and when it was issued. */}
                    <Card className="border border-border shadow-none">
                        <CardContent className="p-4">
                            <h2 className="font-heading text-lg font-bold mb-2">
                                {t('invoices.section_details')}
                            </h2>
                            <div className="max-w-md">
                                <MetaRow
                                    label={t('invoices.meta_vat_treatment')}
                                    value={invoice.vat_treatment
                                        // Reuses the Customers wording so the two
                                        // screens name the same thing identically.
                                        // `defaultValue` keeps a stored value we do
                                        // not have a label for from printing a raw
                                        // dot-key at the user.
                                        ? t(`clients.vat_treatment_${invoice.vat_treatment}`, {
                                            defaultValue: invoice.vat_treatment,
                                        })
                                        : ''}
                                />
                                <MetaRow
                                    label={t('invoices.meta_issued_at')}
                                    value={invoice.issued_at ? formatDate(invoice.issued_at) : ''}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <InvoicePdfFrame
                        invoiceId={invoice.id}
                        onDownload={handleDownload}
                        downloading={downloading}
                    />
                </>
            )}

            {showSheet && (
                <>
                    {/* Currency is a DOCUMENT setting whose effect is visible on
                        the sheet (every money suffix, and the `Moms kr` header),
                        but it is a control rather than a printed field, so it
                        sits in the chrome above the page instead of inside it. */}
                    <div className="flex flex-wrap items-center gap-3">
                        <Label htmlFor="sheet-currency" className="label-uppercase">
                            {t('invoices.meta_currency')}
                        </Label>
                        <Select
                            value={form.currency}
                            onValueChange={(value) => setFormField('currency', value)}
                        >
                            <SelectTrigger id="sheet-currency" className="w-32" data-testid="sheet-currency">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CURRENCIES.map((code) => (
                                    <SelectItem key={code} value={code}>{code}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* A zero-rated treatment OVERWRITES every line's rate at
                        save time — said up front here too, because the sheet's
                        VAT column is where an admin would otherwise type 25 and
                        watch it turn into 0 with no explanation. */}
                    {invoice.vat_treatment && invoice.vat_treatment !== 'domestic' && (
                        <p className="text-xs text-muted-foreground" data-testid="sheet-zero-rated-notice">
                            {t('invoices.zero_rated_notice', {
                                treatment: t(`clients.vat_treatment_${invoice.vat_treatment}`, {
                                    defaultValue: invoice.vat_treatment,
                                }),
                            })}
                        </p>
                    )}

                    <InvoiceA4Sheet
                        invoice={invoice}
                        form={form}
                        lines={lines}
                        lineErrors={lineErrors}
                        dirty={dirty}
                        currency={currency}
                        unitOptions={unitOptions}
                        totalsPreview={totalsPreview}
                        onFormField={setFormField}
                        onLineField={setLineField}
                        onAddLine={addLine}
                        onRemoveLine={removeLine}
                        onMoveLine={moveLine}
                    />
                </>
            )}

            {/* Parties */}
            {showForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border border-border shadow-none">
                    <CardContent className="p-4 space-y-3">
                        <AddressBlock
                            title={t('invoices.section_bill_to')}
                            lines={[
                                client.name,
                                client.billing_address?.street,
                                [client.billing_address?.postal_code, client.billing_address?.city]
                                    .filter(Boolean).join(' '),
                                client.billing_address?.country,
                            ]}
                        />
                        {/* `delivery_same_as_billing` is the source of truth — the
                            delivery block is shown only when it really differs. */}
                        {client.delivery_same_as_billing === false && (
                            <AddressBlock
                                title={t('invoices.section_deliver_to')}
                                lines={[
                                    client.delivery_address?.street,
                                    [client.delivery_address?.postal_code, client.delivery_address?.city]
                                        .filter(Boolean).join(' '),
                                    client.delivery_address?.country,
                                ]}
                            />
                        )}
                    </CardContent>
                </Card>

                <Card className="border border-border shadow-none">
                    <CardContent className="p-4 space-y-3">
                        <AddressBlock
                            title={t('invoices.section_from')}
                            lines={[company.name, company.legal_address, company.phone, company.email, company.website]}
                        />
                        <div>
                            <MetaRow label={t('invoices.meta_org_number')} value={company.org_number} />
                            <MetaRow label={t('invoices.meta_vat_number')} value={company.vat_number} />
                            <MetaRow label={t('invoices.meta_bankgiro')} value={company.bankgiro} />
                            <MetaRow label={t('invoices.meta_plusgiro')} value={company.plusgiro} />
                            <MetaRow label={t('invoices.meta_iban')} value={company.iban} />
                            <MetaRow label={t('invoices.meta_bic')} value={company.bic} />
                            {company.approved_for_f_tax && (
                                <MetaRow label={t('invoices.meta_f_tax')} value={t('common.yes')} />
                            )}
                        </div>
                        {isDraft && company.name && (
                            <p className="text-xs text-muted-foreground" data-testid="company-preview-live-notice">
                                {t('invoices.company_preview_live_notice')}
                            </p>
                        )}
                        {isDraft && !company.name && (
                            <p className="text-xs text-muted-foreground">
                                {t('invoices.company_snapshot_at_issue')}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>
            )}

            {/* Meta — the stacked-form surface only. An ISSUED invoice's meta
                is read off the archived PDF above; re-rendering it here would
                be the second renderer KD-081d exists to prevent. */}
            {showForm && (
            <Card className="border border-border shadow-none">
                <CardContent className="p-4 space-y-4">
                    <h2 className="font-heading text-lg font-bold">{t('invoices.section_details')}</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div>
                                <Label htmlFor="invoice-date" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_invoice_date')}
                                </Label>
                                <Input
                                    id="invoice-date"
                                    type="date"
                                    value={form.invoice_date}
                                    onChange={(e) => setFormField('invoice_date', e.target.value)}
                                    data-testid="invoice-date-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-due-date" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_due_date')}
                                </Label>
                                <Input
                                    id="invoice-due-date"
                                    type="date"
                                    value={form.due_date}
                                    onChange={(e) => setFormField('due_date', e.target.value)}
                                    data-testid="invoice-due-date-input"
                                />
                                {!form.due_date && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('invoices.due_date_from_terms', {
                                            days: client.payment_terms_days ?? 30,
                                        })}
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="invoice-delivery-date" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_delivery_date')}
                                </Label>
                                <Input
                                    id="invoice-delivery-date"
                                    type="date"
                                    value={form.delivery_date}
                                    onChange={(e) => setFormField('delivery_date', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-our-reference" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_our_reference')}
                                </Label>
                                <Input
                                    id="invoice-our-reference"
                                    value={form.our_reference}
                                    onChange={(e) => setFormField('our_reference', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-their-reference" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_their_reference')}
                                </Label>
                                <Input
                                    id="invoice-their-reference"
                                    value={form.their_reference}
                                    onChange={(e) => setFormField('their_reference', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-your-order-no" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_your_order_no')}
                                </Label>
                                <Input
                                    id="invoice-your-order-no"
                                    value={form.your_order_no}
                                    onChange={(e) => setFormField('your_order_no', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-reference-no" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_reference_no')}
                                </Label>
                                <Input
                                    id="invoice-reference-no"
                                    value={form.reference_no}
                                    onChange={(e) => setFormField('reference_no', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="invoice-currency" className="label-uppercase mb-2 block">
                                    {t('invoices.meta_currency')}
                                </Label>
                                <Select
                                    value={form.currency}
                                    onValueChange={(value) => setFormField('currency', value)}
                                >
                                    <SelectTrigger id="invoice-currency" data-testid="invoice-currency">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map((code) => (
                                            <SelectItem key={code} value={code}>{code}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Lines — the stacked-form surface. Draft only: an ISSUED
                invoice's lines are read off the archived PDF above. */}
            {showForm && (
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        <h2 className="font-heading text-lg font-bold">{t('invoices.section_lines')}</h2>
                        <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            {t('invoices.add_line')}
                        </Button>
                    </div>

                    {/* A zero-rated treatment OVERWRITES every line's rate at
                        save time (services/invoicing.py::apply_vat_treatment), so
                        saying so up front beats letting an admin type 25 and
                        watch it turn into 0 with no explanation. */}
                    {invoice.vat_treatment && invoice.vat_treatment !== 'domestic' && (
                        <p className="px-4 py-2 text-xs text-muted-foreground border-b border-border">
                            {t('invoices.zero_rated_notice', {
                                treatment: t(`clients.vat_treatment_${invoice.vat_treatment}`, {
                                    defaultValue: invoice.vat_treatment,
                                }),
                            })}
                        </p>
                    )}

                    {lines.length === 0 ? (
                            <p className="p-8 text-center text-muted-foreground">{t('invoices.no_lines_yet')}</p>
                        ) : (
                            <div className="p-3 space-y-3">
                                {lines.map((line, index) => {
                                    /* The two money columns the PDF prints and
                                       this form never used to show: `Moms kr`
                                       (the engine's per-line `vat_amount`) and
                                       `Belopp` (`line_total_incl_vat` — the line
                                       total INCLUDING VAT, not the net).

                                       While the draft is CLEAN the saved figures
                                       are this row's figures. Once anything is
                                       edited the row/save correspondence can no
                                       longer be trusted — a reorder or a delete
                                       shifts every index — so the cells switch to
                                       `previewLineMoney`, which reproduces
                                       `services.invoicing.line_money` exactly and
                                       is rendered as a visible preview until the
                                       save comes back. Nothing here is summed. */
                                    const savedLine = (invoice.lines || [])[index] || null;
                                    const preview = dirty
                                        ? previewLineMoney(line, invoice.vat_treatment)
                                        : null;
                                    const vatAmount = dirty ? preview?.vat : savedLine?.vat_amount;
                                    const lineTotal = dirty ? preview?.total : savedLine?.line_total_incl_vat;

                                    return (
                                    <div
                                        key={index}
                                        className="border border-border p-3 grid grid-cols-1 sm:grid-cols-12 gap-3"
                                        data-testid={`invoice-line-${index}`}
                                    >
                                        <div className="sm:col-span-12 lg:col-span-4">
                                            <Label htmlFor={`line-${index}-designation`} className="label-uppercase mb-2 block text-xs">
                                                {t('invoices.col_designation')}
                                            </Label>
                                            <Input
                                                id={`line-${index}-designation`}
                                                value={line.designation}
                                                onChange={(e) => setLineField(index, 'designation', e.target.value)}
                                                data-testid={`line-${index}-designation`}
                                            />
                                            {lineErrors[index]?.designation && (
                                                <p className="text-xs text-destructive mt-1">{lineErrors[index].designation}</p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-3 lg:col-span-2">
                                            <Label htmlFor={`line-${index}-quantity`} className="label-uppercase mb-2 block text-xs">
                                                {t('invoices.col_quantity')}
                                            </Label>
                                            <Input
                                                id={`line-${index}-quantity`}
                                                inputMode="decimal"
                                                value={line.quantity}
                                                onChange={(e) => setLineField(index, 'quantity', e.target.value)}
                                                data-testid={`line-${index}-quantity`}
                                            />
                                            {lineErrors[index]?.quantity && (
                                                <p className="text-xs text-destructive mt-1">{lineErrors[index].quantity}</p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-3 lg:col-span-2">
                                            <Label htmlFor={`line-${index}-unit`} className="label-uppercase mb-2 block text-xs">
                                                {t('invoices.col_unit')}
                                            </Label>
                                            <Select
                                                value={line.unit || ''}
                                                onValueChange={(value) => setLineField(index, 'unit', value)}
                                            >
                                                <SelectTrigger id={`line-${index}-unit`} data-testid={`line-${index}-unit`}>
                                                    <SelectValue placeholder={t('invoices.col_unit')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {unitOptions.map((unit) => (
                                                        <SelectItem key={unit} value={unit}>{unitLabel(unit)}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="sm:col-span-3 lg:col-span-2">
                                            <Label htmlFor={`line-${index}-price`} className="label-uppercase mb-2 block text-xs">
                                                {t('invoices.col_price')}
                                            </Label>
                                            <Input
                                                id={`line-${index}-price`}
                                                inputMode="decimal"
                                                value={line.price_per_unit}
                                                onChange={(e) => setLineField(index, 'price_per_unit', e.target.value)}
                                                data-testid={`line-${index}-price`}
                                            />
                                            {lineErrors[index]?.price_per_unit && (
                                                <p className="text-xs text-destructive mt-1">{lineErrors[index].price_per_unit}</p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-3 lg:col-span-2">
                                            <Label htmlFor={`line-${index}-vat`} className="label-uppercase mb-2 block text-xs">
                                                {t('invoices.col_vat_rate')}
                                            </Label>
                                            <Input
                                                id={`line-${index}-vat`}
                                                inputMode="decimal"
                                                value={line.vat_rate}
                                                onChange={(e) => setLineField(index, 'vat_rate', e.target.value)}
                                                data-testid={`line-${index}-vat`}
                                            />
                                            {lineErrors[index]?.vat_rate && (
                                                <p className="text-xs text-destructive mt-1">{lineErrors[index].vat_rate}</p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-12 flex flex-wrap items-baseline justify-end gap-x-6 gap-y-1 pt-1 border-t border-border">
                                            <span className="text-xs text-muted-foreground">
                                                {vatAmountHeader}{' '}
                                                <span
                                                    className={`font-mono text-foreground${dirty ? ' italic text-muted-foreground' : ''}`}
                                                    data-testid={`line-${index}-vat-amount`}
                                                >
                                                    {vatAmount == null ? '—' : formatMoney(vatAmount, currency)}
                                                </span>
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {t('invoices.col_line_total')}{' '}
                                                <span
                                                    className={`font-mono text-foreground${dirty ? ' italic text-muted-foreground' : ''}`}
                                                    data-testid={`line-${index}-line-total`}
                                                >
                                                    {lineTotal == null ? '—' : formatMoney(lineTotal, currency)}
                                                </span>
                                            </span>
                                        </div>

                                        <div className="sm:col-span-12 flex items-center justify-between gap-2 pt-1">
                                            {line.source ? (
                                                <span className="text-xs text-muted-foreground">
                                                    {t(`invoices.source_${line.source.source_type}`, {
                                                        count: line.source.source_ids?.length || 0,
                                                    })}
                                                </span>
                                            ) : <span />}
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => moveLine(index, -1)}
                                                    disabled={index === 0}
                                                    aria-label={t('invoices.move_line_up')}
                                                >
                                                    <ChevronUp className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => moveLine(index, 1)}
                                                    disabled={index === lines.length - 1}
                                                    aria-label={t('invoices.move_line_down')}
                                                >
                                                    <ChevronDown className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeLine(index)}
                                                    aria-label={t('invoices.remove_line')}
                                                    data-testid={`remove-line-${index}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                    )}

                    {dirty && (
                        <p
                            className="px-4 pb-4 text-xs text-muted-foreground"
                            data-testid="line-preview-hint"
                        >
                            {t('invoices.sheet_preview_hint')}
                        </p>
                    )}
                </CardContent>
            </Card>
            )}

            {/* The four custom text blocks (PRD 10.14, Task 21.16).
                Editable ONLY on a draft — the same gate `notes` has always had,
                because these print on the document and an issued document takes
                no edits. Laid out in the order the PDF prints them, and the
                order is spelled out above them: four separate fields only make
                sense if you can see where each one lands. */}
            {showForm && (
            <Card className="border border-border shadow-none">
                <CardContent className="p-4 space-y-4">
                    <div>
                        <h2 className="font-heading text-lg font-bold">{t('invoices.section_text_blocks')}</h2>
                        <p className="text-sm text-muted-foreground mt-1">{t('invoices.text_blocks_intro')}</p>
                        <p className="text-xs text-muted-foreground mt-1" data-testid="text-blocks-order">
                            {t('invoices.text_blocks_order')}
                        </p>
                    </div>

                    <div className="space-y-4">
                            {TEXT_BLOCKS.map(({ field, rows }) => (
                                <div key={field}>
                                    <Label htmlFor={`invoice-${field}`} className="label-uppercase mb-2 block">
                                        {t(`invoices.block_${field}_label`)}
                                    </Label>
                                    <Textarea
                                        id={`invoice-${field}`}
                                        rows={rows}
                                        maxLength={TEXT_BLOCK_MAX_LENGTH}
                                        value={form[field]}
                                        onChange={(e) => setFormField(field, e.target.value)}
                                        placeholder={t('invoices.block_placeholder')}
                                        data-testid={`invoice-${field}`}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t(`invoices.block_${field}_hint`)}
                                    </p>
                                </div>
                            ))}
                        </div>
                </CardContent>
            </Card>
            )}

            {/* Totals — every figure straight off the document. Draft only:
                an ISSUED invoice's totals are read off the archived PDF above,
                and re-rendering them here would be a second place they could be
                wrong. */}
            {showForm && (
            <Card className="border border-border shadow-none">
                <CardContent className="p-4 space-y-2">
                    <h2 className="font-heading text-lg font-bold mb-2">{t('invoices.section_totals')}</h2>

                    {dirty && (
                        <p className="text-xs text-muted-foreground" data-testid="totals-stale-notice">
                            {t('invoices.totals_recalculated_on_save')}
                        </p>
                    )}

                    <div className="max-w-md ml-auto space-y-1">
                        <div className="flex justify-between text-sm">
                            <span>{t('invoices.total_excl_vat')}</span>
                            <span className="font-mono">{formatMoney(invoice.total_excl_vat, invoice.currency)}</span>
                        </div>

                        {/* Per-rate bands, printed only when the invoice really
                            mixes rates — the single-rate case is already covered
                            by the VAT total row below. */}
                        {(invoice.vat_by_rate || []).length > 1 && invoice.vat_by_rate.map((band) => (
                            <div key={band.vat_rate} className="flex justify-between text-sm text-muted-foreground">
                                <span>{t('invoices.vat_at_rate', { rate: formatPercent(band.vat_rate) })}</span>
                                <span className="font-mono">{formatMoney(band.vat, invoice.currency)}</span>
                            </div>
                        ))}

                        <div className="flex justify-between text-sm">
                            <span>{t('invoices.vat_total')}</span>
                            <span className="font-mono">{formatMoney(invoice.vat_total, invoice.currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span>{t('invoices.rounding')}</span>
                            <span className="font-mono">{formatMoney(invoice.rounding, invoice.currency)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                            <span>{t('invoices.total_incl_vat')}</span>
                            <span className="font-mono" data-testid="invoice-total">
                                {formatMoney(invoice.total_incl_vat, invoice.currency)}
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Attachments & Receipts */}
            <InvoiceAttachmentsCard
                invoice={invoice}
                onAttachmentsUpdated={(updatedInvoice) => {
                    setInvoice((prev) =>
                        prev ? { ...prev, attachments: updatedInvoice.attachments || [] } : updatedInvoice
                    );
                }}
                isDraft={isDraft}
            />

            {/* Registered payments (PRD 10.12, Task 21.18).
                Shown whenever the document can take a payment OR already
                carries one — an issued invoice with nothing registered still
                needs the way in, and a credited invoice still has to show the
                payments it received. */}
            {(payable || (invoice.payments || []).length > 0) && (
                <Card className="border border-border shadow-none" data-testid="payments-card">
                    <CardContent className="p-0">
                        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <h2 className="font-heading text-lg font-bold">{t('invoices.payments_title')}</h2>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('invoices.payments_reversal_hint')}
                                </p>
                            </div>
                            {payable && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPaymentOpen(true)}
                                    data-testid="register-payment-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('invoices.register_payment')}
                                </Button>
                            )}
                        </div>

                        {(invoice.payments || []).length === 0 ? (
                            <p className="p-8 text-center text-muted-foreground">
                                {t('invoices.payments_none')}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('invoices.payments_col_date')}</th>
                                            <th className="text-right">{t('invoices.payments_col_amount')}</th>
                                            <th>{t('invoices.payments_col_method')}</th>
                                            <th>{t('invoices.payments_col_reference')}</th>
                                            <th className="text-right">{t('invoices.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.payments.map((payment) => {
                                            const voided = isPaymentVoided(payment);
                                            return (
                                                <tr
                                                    key={payment.id}
                                                    className={voided ? 'text-muted-foreground' : ''}
                                                    data-testid={`payment-row-${payment.id}`}
                                                >
                                                    <td className={voided ? 'line-through' : ''}>
                                                        {formatDate(payment.paid_date)}
                                                    </td>
                                                    <td
                                                        className={`text-right font-mono ${voided ? 'line-through' : ''}`}
                                                        data-testid={`payment-amount-${payment.id}`}
                                                    >
                                                        {formatMoney(payment.amount, invoice.currency)}
                                                    </td>
                                                    <td className={voided ? 'line-through' : ''}>
                                                        {paymentMethodLabel(payment.method) || '—'}
                                                    </td>
                                                    <td className="text-sm">
                                                        <span className={`font-mono ${voided ? 'line-through' : ''}`}>
                                                            {payment.reference || '—'}
                                                        </span>
                                                        {payment.note && (
                                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                                {payment.note}
                                                            </p>
                                                        )}
                                                        {/* A void is only readable if its reason is: it is
                                                            the one thing that explains, a year later, why a
                                                            settled invoice went back to outstanding. */}
                                                        {voided && payment.void_reason && (
                                                            <p className="text-xs mt-0.5">
                                                                {t('invoices.payment_void_reason', {
                                                                    reason: payment.void_reason,
                                                                })}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="text-right whitespace-nowrap">
                                                        {voided ? (
                                                            /* The row stays, struck through and plainly
                                                               excluded — never hidden. It is a fact about
                                                               the money that happened and was undone. */
                                                            <Badge
                                                                variant="outline"
                                                                data-testid={`payment-voided-${payment.id}`}
                                                            >
                                                                {t('invoices.payment_reversed_badge')}
                                                            </Badge>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setVoidTarget(payment)}
                                                                data-testid={`void-payment-${payment.id}`}
                                                            >
                                                                <Ban className="w-4 h-4 mr-2" />
                                                                {t('invoices.void_payment')}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* The balance. `amount_due` is SERVER-COMPUTED and on
                            every invoice response — nothing here subtracts one
                            figure from another. */}
                        {typeof invoice.amount_due === 'number' && (
                            <div className="p-4 border-t border-border space-y-2">
                                <div className="max-w-md ml-auto space-y-1">
                                    <div className="flex justify-between text-sm text-muted-foreground">
                                        <span>{t('invoices.total_incl_vat')}</span>
                                        <span className="font-mono">
                                            {formatMoney(invoice.total_incl_vat, invoice.currency)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                                        <span>{t('invoices.amount_due')}</span>
                                        <span
                                            className={`font-mono ${invoice.amount_due < 0 ? 'text-destructive' : ''}`}
                                            data-testid="invoice-amount-due"
                                        >
                                            {formatMoney(invoice.amount_due, invoice.currency)}
                                        </span>
                                    </div>
                                </div>

                                {/* A negative balance means more was registered
                                    than the invoice asked for. It is shown as it
                                    stands rather than clamped at zero: the excess
                                    is real money the customer is owed back, and
                                    hiding it is how it goes unrefunded. */}
                                {invoice.amount_due < 0 && (
                                    <p className="text-sm text-destructive" data-testid="overpaid-notice">
                                        {t('invoices.overpaid_notice')}
                                    </p>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Save bar */}
            {isEditable && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                    {dirty && (
                        <span className="text-sm text-muted-foreground">{t('invoices.unsaved_changes')}</span>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={saving || !dirty}
                        data-testid="save-draft-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {invoice?.status === 'draft' ? t('invoices.save_draft') : t('common.save', 'Save')}
                    </Button>
                </div>
            )}

            {/* Mark as Sent confirmation */}
            <Dialog open={markSentOpen} onOpenChange={setMarkSentOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('invoices.mark_as_sent_confirm_title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('invoices.mark_as_sent_confirm_desc')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMarkSentOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleMarkSent}
                            disabled={markingSent}
                            data-testid="confirm-mark-sent-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {markingSent && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('invoices.mark_as_sent_confirm_action')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Issue confirmation — the consequence stated plainly */}
            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('invoices.issue_confirm_title')}
                        </DialogTitle>
                        <DialogDescription>{t('invoices.issue_confirm_body')}</DialogDescription>
                    </DialogHeader>
                    <ul className="text-sm list-disc pl-5 space-y-1">
                        <li>{t('invoices.issue_consequence_number')}</li>
                        <li>{t('invoices.issue_consequence_immutable')}</li>
                        <li>{t('invoices.issue_consequence_sources')}</li>
                    </ul>
                    {dirty && (
                        <p className="text-sm text-destructive">{t('invoices.issue_unsaved_warning')}</p>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIssueOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleIssue} disabled={issuing} data-testid="confirm-issue-btn">
                            {issuing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('invoices.issue_confirm_cta')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Discard draft */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('invoices.discard_confirm_title')}
                        </DialogTitle>
                        <DialogDescription>{t('invoices.discard_confirm_body')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleting}
                            data-testid="confirm-discard-btn"
                        >
                            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('invoices.discard_confirm_cta')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SendInvoiceDialog
                open={sendOpen}
                onOpenChange={setSendOpen}
                invoice={invoice}
                onSent={applyInvoice}
                onDownload={handleDownload}
                downloading={downloading}
            />

            <RegisterPaymentDialog
                open={paymentOpen}
                onOpenChange={setPaymentOpen}
                invoice={invoice}
                onRegistered={applyInvoice}
            />

            <VoidPaymentDialog
                payment={voidTarget}
                onOpenChange={(open) => { if (!open) setVoidTarget(null); }}
                invoice={invoice}
                onVoided={(doc) => { applyInvoice(doc); setVoidTarget(null); }}
            />

            <CreditInvoiceDialog
                open={creditOpen}
                onOpenChange={setCreditOpen}
                invoice={invoice}
                onCredited={handleCredited}
                onIssueFailed={handleCreditIssueFailed}
            />
        </div>
    );
}

/**
 * Register one payment against an issued invoice (PRD 10.12).
 *
 * TWO RULES SHAPE THIS DIALOG, and both are backend decisions it honours
 * rather than reinterprets:
 *
 * 1. THE AMOUNT IS NEVER SENT EMPTY. `amount` is typed `float` on the wire, so
 *    a cleared field would be an untranslatable 422. A blank or unparseable
 *    amount is a FIELD-LEVEL error here and the request is not made.
 *
 * 2. AN OVERPAYMENT IS NOT BLOCKED. There is deliberately no check that the
 *    amount fits inside the outstanding balance. The backend accepts an
 *    overpayment in full and lets `amount_due` go negative, because the money
 *    really did arrive; a guard here would leave the admin choosing between an
 *    invoice that stays unsettled and typing a number the bank does not show.
 *    A mistake is recovered by voiding the payment, which leaves both the
 *    mistake and the correction on the record.
 *
 * Nothing on this form computes money. The amount is what the admin read off
 * the bank statement, and the resulting balance comes back from the server.
 */
function RegisterPaymentDialog({ open, onOpenChange, invoice, onRegistered }) {
    const { t } = useTranslation();

    const [amount, setAmount] = useState('');
    const [paidDate, setPaidDate] = useState(todayField());
    const [method, setMethod] = useState(NO_METHOD);
    const [reference, setReference] = useState('');
    const [note, setNote] = useState('');
    const [amountError, setAmountError] = useState(null);
    const [saving, setSaving] = useState(false);

    // Reset each time the dialog opens so a previous entry can never be
    // re-submitted against a different payment by accident.
    useEffect(() => {
        if (!open) return;
        setAmount('');
        setPaidDate(todayField());
        setMethod(NO_METHOD);
        setReference('');
        setNote('');
        setAmountError(null);
    }, [open]);

    const handleSubmit = async () => {
        const parsed = parseDecimal(amount);
        if (parsed === null) {
            setAmountError(t('invoices.payment_amount_required'));
            return;
        }
        if (parsed <= 0) {
            // Mirrors services.invoicing.new_payment. A reversal is a void, not
            // a negative payment.
            setAmountError(t('error.invoices.invalid_payment_amount'));
            return;
        }
        if (!paidDate) {
            toast.error(t('error.invoices.invalid_payment_date'));
            return;
        }

        setSaving(true);
        try {
            const res = await registerInvoicePayment(invoice.id, {
                amount: parsed,
                paid_date: paidDate,
                // A stable key or null — never "".
                method: method === NO_METHOD ? null : method,
                reference: reference.trim() || null,
                note: note.trim(),
            });
            onRegistered(res.data);
            onOpenChange(false);
            toast.success(
                t('invoices.payment_registered_toast', {
                    amount: formatMoney(parsed, invoice.currency),
                })
            );
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_register_payment')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {t('invoices.register_payment_title')}
                    </DialogTitle>
                    <DialogDescription>{t('invoices.register_payment_body')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="payment-amount" className="label-uppercase mb-2 block">
                                {t('invoices.payment_amount_label')}
                            </Label>
                            <Input
                                id="payment-amount"
                                inputMode="decimal"
                                value={amount}
                                onChange={(e) => { setAmount(e.target.value); setAmountError(null); }}
                                data-testid="payment-amount-input"
                            />
                            {amountError ? (
                                <p className="text-xs text-destructive mt-1" data-testid="payment-amount-error">
                                    {amountError}
                                </p>
                            ) : typeof invoice.amount_due === 'number' ? (
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('invoices.payment_amount_hint', {
                                        balance: formatMoney(invoice.amount_due, invoice.currency),
                                    })}
                                </p>
                            ) : null}
                        </div>
                        <div>
                            <Label htmlFor="payment-date" className="label-uppercase mb-2 block">
                                {t('invoices.payment_date_label')}
                            </Label>
                            <Input
                                id="payment-date"
                                type="date"
                                value={paidDate}
                                onChange={(e) => setPaidDate(e.target.value)}
                                data-testid="payment-date-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('invoices.payment_date_hint')}
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="payment-method" className="label-uppercase mb-2 block">
                                {t('invoices.payment_method_field_label')}
                            </Label>
                            <Select value={method} onValueChange={setMethod}>
                                <SelectTrigger id="payment-method" data-testid="payment-method-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NO_METHOD}>
                                        {t('invoices.payment_method_unspecified')}
                                    </SelectItem>
                                    {PAYMENT_METHODS.map((key) => (
                                        <SelectItem key={key} value={key}>
                                            {paymentMethodLabel(key)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="payment-reference" className="label-uppercase mb-2 block">
                                {t('invoices.payment_reference_label')}
                            </Label>
                            <Input
                                id="payment-reference"
                                maxLength={PAYMENT_REFERENCE_MAX}
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder={invoice.ocr_reference || ''}
                                data-testid="payment-reference-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('invoices.payment_reference_hint')}
                            </p>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="payment-note" className="label-uppercase mb-2 block">
                            {t('invoices.payment_note_label')}
                        </Label>
                        <Textarea
                            id="payment-note"
                            rows={2}
                            maxLength={PAYMENT_NOTE_MAX}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            data-testid="payment-note-input"
                        />
                    </div>

                    <p className="text-xs text-muted-foreground">
                        {t('invoices.payment_overpayment_hint')}
                    </p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={saving} data-testid="confirm-payment-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {t('invoices.register_payment_cta')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Reverse a payment (PRD 10.12).
 *
 * The word on the button is "Reverse", never "Delete" and never "Edit",
 * because that is what happens: the row stays in `payments[]` forever, marked
 * with who reversed it, when and why, and simply stops counting towards
 * settlement. The dialog says so plainly — an admin who believes they are
 * erasing a row would be surprised to find it still on the invoice, and
 * surprise is how a record stops being trusted.
 *
 * The reason is optional (the backend defaults it to ""), but it is the only
 * thing that explains a year later why a settled invoice went back to
 * outstanding, so the form asks for it rather than burying it.
 */
function VoidPaymentDialog({ payment, onOpenChange, invoice, onVoided }) {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const open = Boolean(payment);

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const handleVoid = async () => {
        setSaving(true);
        try {
            const res = await voidInvoicePayment(invoice.id, payment.id, reason.trim());
            onVoided(res.data);
            toast.success(t('invoices.payment_voided_toast'));
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_void_payment')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {t('invoices.void_payment_title')}
                    </DialogTitle>
                    <DialogDescription>
                        {payment
                            ? t('invoices.void_payment_body', {
                                amount: formatMoney(payment.amount, invoice.currency),
                                date: formatDate(payment.paid_date),
                            })
                            : ''}
                    </DialogDescription>
                </DialogHeader>

                <p className="text-sm">{t('invoices.void_payment_kept_notice')}</p>

                <div>
                    <Label htmlFor="void-reason" className="label-uppercase mb-2 block">
                        {t('invoices.void_reason_label')}
                    </Label>
                    <Textarea
                        id="void-reason"
                        rows={3}
                        maxLength={VOID_REASON_MAX}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t('invoices.void_reason_placeholder')}
                        data-testid="void-reason-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('invoices.void_reason_hint')}
                    </p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleVoid}
                        disabled={saving}
                        data-testid="confirm-void-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {t('invoices.void_payment_cta')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Issue a credit note against an issued invoice (PRD 10.8).
 *
 * This is the heaviest action on the screen and the dialog is written to match:
 * it is irreversible, it withdraws a document a customer has been sent, it
 * burns a number from the gapless series, and it puts the billed work back in
 * the billable pool. All four are stated before the button, because an admin
 * who did not expect the fourth will re-invoice work they thought was gone.
 *
 * PARTIAL CREDITS DO NOT EXIST here, so no line picker is offered. The backend
 * refuses `line_ids` outright rather than quietly crediting in full; offering a
 * selection the API would reject would be a worse lie than not offering one.
 */
function CreditInvoiceDialog({ open, onOpenChange, invoice, onCredited, onIssueFailed }) {
    const { t } = useTranslation();

    const [invoiceDate, setInvoiceDate] = useState(todayField());
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setInvoiceDate(todayField());
        setNotes('');
    }, [open]);

    const handleCredit = async () => {
        setSaving(true);
        try {
            // ONLY these two keys: the request model is `extra="forbid"`.
            // `notes` is omitted rather than sent empty so the credit note
            // inherits the original's text — sending "" would blank it.
            const options = { invoice_date: invoiceDate };
            if (notes.trim()) options.notes = notes.trim();

            const res = await creditInvoice(invoice.id, options);
            onCredited(res.data);
        } catch (error) {
            const detail = error?.response?.data?.detail;

            // The one failure that is really a PARTIAL SUCCESS: the original
            // is already credited and already linked to a draft credit note.
            // It must never read as "nothing happened, try again" — a second
            // credit is refused, and the repair is to issue the draft.
            if (detail === 'error.invoices.credit_note_issue_failed') {
                await onIssueFailed();
                toast.warning(t('error.invoices.credit_note_issue_failed'), { duration: 30000 });
                return;
            }

            toast.error(getApiErrorMessage(error, t('invoices.failed_credit')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {t('invoices.credit_confirm_title')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('invoices.credit_confirm_body', {
                            number: invoice.invoice_number || '',
                        })}
                    </DialogDescription>
                </DialogHeader>

                <ul className="text-sm list-disc pl-5 space-y-1">
                    <li>{t('invoices.credit_consequence_withdrawn')}</li>
                    <li>{t('invoices.credit_consequence_number')}</li>
                    <li>{t('invoices.credit_consequence_sources')}</li>
                    <li>{t('invoices.credit_consequence_irreversible')}</li>
                </ul>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="credit-date" className="label-uppercase mb-2 block">
                            {t('invoices.credit_date_label')}
                        </Label>
                        <Input
                            id="credit-date"
                            type="date"
                            value={invoiceDate}
                            onChange={(e) => setInvoiceDate(e.target.value)}
                            data-testid="credit-date-input"
                        />
                    </div>
                </div>

                <div>
                    <Label htmlFor="credit-notes" className="label-uppercase mb-2 block">
                        {t('invoices.credit_notes_label')}
                    </Label>
                    <Textarea
                        id="credit-notes"
                        rows={3}
                        maxLength={TEXT_BLOCK_MAX_LENGTH}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('invoices.credit_notes_placeholder')}
                        data-testid="credit-notes-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('invoices.credit_notes_hint')}
                    </p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleCredit}
                        disabled={saving || !invoiceDate}
                        data-testid="confirm-credit-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {t('invoices.credit_confirm_cta')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * The send dialog — wired to POST /invoices/{id}/send (Task 21.12, PRD 10.11).
 *
 * THE ONE RULE HERE: this dialog never marks anything as sent by itself. The
 * endpoint is fail-closed — a failure is always an HTTP error, and a 200 body
 * carries the already re-read document with its sent stamp on it. So a success
 * applies `response.invoice` verbatim (no re-fetch) and every failure leaves
 * the displayed invoice exactly as it was. A screen that showed "Sent" for an
 * email that never left would make the admin stop chasing a customer who was
 * never billed (Principle 2).
 *
 * The one failure where that rule inverts is `send_stamp_failed`: the email DID
 * go out and only the stamp failed to write. It is surfaced as a warning that
 * says so and tells the admin not to resend — the opposite advice from every
 * other error here, which is exactly why it is handled apart from them.
 *
 * Copy-addresses and download stay: sending the PDF from one's own mail client
 * is still a legitimate way to invoice, and remains one click away.
 */

/**
 * Attachments & Receipts card for an invoice.
 * Allows uploading/deleting custom attachments in draft mode,
 * and downloading any attached files or billable receipts.
 */
function InvoiceAttachmentsCard({ invoice, onAttachmentsUpdated, isDraft }) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const attachments = invoice?.attachments || [];

    if (!isDraft && attachments.length === 0) {
        return null;
    }

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await uploadInvoiceAttachment(invoice.id, file);
            if (res.data) {
                onAttachmentsUpdated?.(res.data);
            }
            toast.success(t('invoices.attachment_uploaded'));
        } catch (err) {
            toast.error(getApiErrorMessage(err, t('invoices.failed_upload_attachment')));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (attId) => {
        if (!window.confirm(t('invoices.delete_attachment_confirm'))) return;
        setDeletingId(attId);
        try {
            const res = await deleteInvoiceAttachment(invoice.id, attId);
            if (res.data) {
                onAttachmentsUpdated?.(res.data);
            }
            toast.success(t('invoices.attachment_deleted'));
        } catch (err) {
            toast.error(getApiErrorMessage(err, t('invoices.failed_delete_attachment')));
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (att) => {
        setDownloadingId(att.id);
        try {
            const res = await downloadInvoiceAttachment(invoice.id, att.id);
            const blob = new Blob([res.data], { type: att.content_type || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = att.filename || 'attachment';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setDownloadingId(null);
        }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Card className="border border-border shadow-none" data-testid="invoice-attachments-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="font-heading text-lg font-bold">{t('invoices.section_attachments')}</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {t('invoices.attachments_subtitle')}
                        </p>
                    </div>
                    {isDraft && (
                        <div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".pdf,.png,.jpg,.jpeg"
                                className="hidden"
                                data-testid="invoice-attachment-file-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="upload-attachment-btn"
                            >
                                {uploading ? (
                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                ) : (
                                    <Paperclip className="w-3.5 h-3.5 mr-1.5" />
                                )}
                                {uploading ? t('invoices.uploading_attachment') : t('invoices.upload_attachment')}
                            </Button>
                        </div>
                    )}
                </div>

                {attachments.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">{t('invoices.no_attachments')}</p>
                ) : (
                    <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
                        {attachments.map((att) => (
                            <div
                                key={att.id}
                                className="p-3 flex items-center justify-between gap-3 text-sm hover:bg-muted/30 transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium truncate">{att.name || att.filename}</span>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                            {att.source_type === 'expense' ? t('invoices.source_expense') : t('invoices.source_upload')}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                        <span className="truncate">{att.filename}</span>
                                        {att.file_size > 0 && <span>• {formatSize(att.file_size)}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-2 text-xs"
                                        onClick={() => handleDownload(att)}
                                        disabled={downloadingId === att.id}
                                        title={t('invoices.download_attachment')}
                                    >
                                        {downloadingId === att.id ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <Download className="w-3.5 h-3.5" />
                                        )}
                                    </Button>
                                    {isDraft && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
                                            onClick={() => handleDelete(att.id)}
                                            disabled={deletingId === att.id}
                                            title={t('invoices.delete_attachment')}
                                        >
                                            {deletingId === att.id ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-3.5 h-3.5" />
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function SendInvoiceDialog({ open, onOpenChange, invoice, onSent, onDownload, downloading }) {
    const { t } = useTranslation();
    const client = invoice?.client_snapshot || {};

    // The two addresses are separate fields on purpose — in Swedish practice a
    // customer's contact person and their invoicing inbox are different
    // addresses (PRD 10.1).
    const contactEmail = client.contact_email || '';
    const invoiceEmail = client.invoice_email || '';

    const [useContact, setUseContact] = useState(false);
    const [useInvoiceEmail, setUseInvoiceEmail] = useState(true);
    const [customRecipients, setCustomRecipients] = useState([]);
    const [customEmailInput, setCustomEmailInput] = useState('');
    const [sending, setSending] = useState(false);

    const attachments = useMemo(() => invoice?.attachments || [], [invoice?.attachments]);
    const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);

    // Default to whichever addresses actually exist, re-evaluated each time the
    // dialog opens so a client edit in another tab is picked up.
    useEffect(() => {
        if (!open) return;
        setUseInvoiceEmail(Boolean(invoiceEmail));
        setUseContact(Boolean(contactEmail) && !invoiceEmail);
        setCustomRecipients([]);
        setCustomEmailInput('');
        setSelectedAttachmentIds((invoice?.attachments || []).map((a) => a.id));
    }, [open, contactEmail, invoiceEmail, invoice?.attachments]);

    const recipients = useMemo(() => {
        const list = [
            useInvoiceEmail ? invoiceEmail : null,
            useContact ? contactEmail : null,
            ...customRecipients,
        ].filter(Boolean);
        const seen = new Set();
        const unique = [];
        for (const item of list) {
            const key = item.trim().toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                unique.push(item.trim());
            }
        }
        return unique;
    }, [useInvoiceEmail, invoiceEmail, useContact, contactEmail, customRecipients]);

    const noAddresses = !contactEmail && !invoiceEmail;

    const handleAddRecipient = () => {
        const trimmed = customEmailInput.trim();
        if (!trimmed) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
            toast.error(t('invoices.send_custom_recipient_invalid'));
            return;
        }
        const lower = trimmed.toLowerCase();
        const allKnown = [
            invoiceEmail ? invoiceEmail.toLowerCase() : null,
            contactEmail ? contactEmail.toLowerCase() : null,
            ...customRecipients.map((e) => e.toLowerCase()),
        ].filter(Boolean);

        if (allKnown.includes(lower)) {
            toast.info(t('invoices.send_custom_recipient_duplicate'));
            setCustomEmailInput('');
            return;
        }
        if (recipients.length >= 5) {
            toast.error(t('invoices.send_custom_recipient_limit'));
            return;
        }
        setCustomRecipients((prev) => [...prev, trimmed]);
        setCustomEmailInput('');
    };

    const handleRemoveRecipient = (emailToRemove) => {
        setCustomRecipients((prev) => prev.filter((e) => e !== emailToRemove));
    };

    const copyRecipients = async () => {
        try {
            await navigator.clipboard.writeText(recipients.join(', '));
            toast.success(t('invoices.addresses_copied'));
        } catch {
            toast.error(t('invoices.copy_failed'));
        }
    };

    const handleSend = async () => {
        if (recipients.length === 0) return;
        if (recipients.length > 5) {
            toast.error(t('invoices.send_custom_recipient_limit'));
            return;
        }

        setSending(true);
        try {
            const sendOptions = attachments.length > 0 ? { attachment_ids: selectedAttachmentIds } : undefined;
            const res = await sendInvoice(invoice.id, recipients, sendOptions);
            const data = res.data || {};
            // The endpoint returns the re-read document with its sent stamp
            // already on it — applying it is the whole update, and re-fetching
            // would only risk showing a staler copy.
            if (data.invoice) onSent(data.invoice);
            onOpenChange(false);
            toast.success(
                t('invoices.sent_toast', {
                    recipients: (data.sent_to || recipients).join(', '),
                })
            );
        } catch (error) {
            const detail = error?.response?.data?.detail;

            // `send_stamp_failed` is the one error that means the email WAS
            // delivered. It must not read as "it failed, try again" — a blind
            // resend mails the customer the same invoice twice.
            if (detail === 'error.invoices.send_stamp_failed') {
                onOpenChange(false);
                toast.warning(t('error.invoices.send_stamp_failed'), { duration: 30000 });
                return;
            }

            // Structured 502/503 details carry the context that makes them
            // actionable (which sender address was refused, what the provider
            // said), which `getApiErrorMessage` would flatten to JSON. Same
            // `detail.error_code` shape as error.fx.unavailable elsewhere.
            const errorCode = detail && typeof detail === 'object' ? detail.error_code : null;
            if (errorCode) {
                toast.error(
                    t(errorCode, {
                        defaultValue: t('invoices.failed_send'),
                        sender_email: detail.sender_email || '',
                        provider_message: detail.provider_message || '',
                    }),
                    { duration: 15000 }
                );
                return;
            }

            toast.error(getApiErrorMessage(error, t('invoices.failed_send')));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {t('invoices.send_dialog_title')}
                    </DialogTitle>
                    <DialogDescription>{t('invoices.send_dialog_body')}</DialogDescription>
                </DialogHeader>

                <fieldset className="space-y-3">
                    <legend className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">
                        {t('invoices.send_recipients')}
                    </legend>
                    {invoiceEmail && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="send-invoice-email"
                                checked={useInvoiceEmail}
                                onCheckedChange={(checked) => {
                                    if (checked && recipients.length >= 5 && !useInvoiceEmail) {
                                        toast.error(t('invoices.send_custom_recipient_limit'));
                                        return;
                                    }
                                    setUseInvoiceEmail(checked === true);
                                }}
                                data-testid="send-invoice-email"
                            />
                            <Label htmlFor="send-invoice-email" className="font-normal cursor-pointer">
                                {t('invoices.send_to_invoice_email')}: {invoiceEmail}
                            </Label>
                        </div>
                    )}
                    {contactEmail && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="send-contact-email"
                                checked={useContact}
                                onCheckedChange={(checked) => {
                                    if (checked && recipients.length >= 5 && !useContact) {
                                        toast.error(t('invoices.send_custom_recipient_limit'));
                                        return;
                                    }
                                    setUseContact(checked === true);
                                }}
                                data-testid="send-contact-email"
                            />
                            <Label htmlFor="send-contact-email" className="font-normal cursor-pointer">
                                {t('invoices.send_to_contact_email')}: {contactEmail}
                            </Label>
                        </div>
                    )}
                    {noAddresses && customRecipients.length === 0 && (
                        <p className="text-sm text-muted-foreground">{t('invoices.send_no_addresses')}</p>
                    )}

                    {customRecipients.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                            {customRecipients.map((email) => (
                                <div
                                    key={email}
                                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border border-border bg-muted/40 text-sm"
                                >
                                    <span className="font-mono text-xs truncate">{email}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                        onClick={() => handleRemoveRecipient(email)}
                                        aria-label={t('invoices.send_custom_recipient_remove', { email })}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="pt-2">
                        <Label htmlFor="custom-recipient-input" className="text-xs font-medium text-muted-foreground">
                            {t('invoices.send_custom_recipient')}
                        </Label>
                        <div className="flex gap-2 mt-1">
                            <Input
                                id="custom-recipient-input"
                                type="email"
                                placeholder={t('invoices.send_custom_recipient_placeholder')}
                                value={customEmailInput}
                                onChange={(e) => setCustomEmailInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddRecipient();
                                    }
                                }}
                                className="h-8 text-sm"
                                data-testid="send-custom-recipient-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs shrink-0"
                                onClick={handleAddRecipient}
                                disabled={!customEmailInput.trim()}
                                data-testid="send-custom-recipient-add-btn"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                {t('invoices.send_custom_recipient_add')}
                            </Button>
                        </div>
                    </div>
                </fieldset>

                {/* Attachments checklist */}
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground font-medium block">
                        {t('invoices.send_attachments_heading')}
                    </Label>
                    <div className="border border-border rounded-md divide-y divide-border bg-muted/20">
                        {/* Invoice PDF itself - always included */}
                        <div className="flex items-center gap-2 p-2.5 text-sm">
                            <Checkbox id="send-att-pdf" checked={true} disabled={true} />
                            <Label htmlFor="send-att-pdf" className="font-normal cursor-default flex-1 truncate">
                                {invoice?.invoice_number ? `${invoice.invoice_number}.pdf` : 'Faktura.pdf'}
                                <span className="text-xs text-muted-foreground ml-2">({t('invoices.send_attachment_main_pdf')})</span>
                            </Label>
                        </div>
                        {/* Additional attachments & receipts */}
                        {attachments.map((att) => {
                            const checked = selectedAttachmentIds.includes(att.id);
                            return (
                                <div key={att.id} className="flex items-center gap-2 p-2.5 text-sm">
                                    <Checkbox
                                        id={`send-att-${att.id}`}
                                        checked={checked}
                                        onCheckedChange={(val) => {
                                            if (val) {
                                                setSelectedAttachmentIds((prev) => [...prev, att.id]);
                                            } else {
                                                setSelectedAttachmentIds((prev) => prev.filter((id) => id !== att.id));
                                            }
                                        }}
                                    />
                                    <Label htmlFor={`send-att-${att.id}`} className="font-normal cursor-pointer flex-1 truncate">
                                        {att.filename}
                                        {att.name && att.name !== att.filename && (
                                            <span className="text-xs text-muted-foreground ml-2">({att.name})</span>
                                        )}
                                    </Label>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Sending it by hand is still a legitimate way to invoice —
                    kept one click away rather than removed now that the button
                    above works. */}
                <div className="border border-border p-3 text-sm space-y-2" data-testid="send-alternatives">
                    <p className="text-muted-foreground">{t('invoices.send_attaches_pdf')}</p>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={copyRecipients}
                            disabled={recipients.length === 0}
                        >
                            {t('invoices.copy_addresses')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={onDownload} disabled={downloading}>
                            {downloading
                                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                : <Download className="w-4 h-4 mr-2" />}
                            {t('invoices.download_pdf')}
                        </Button>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                        {t('common.close')}
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={sending || recipients.length === 0}
                        data-testid="send-confirm-btn"
                    >
                        {sending
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Mail className="w-4 h-4 mr-2" />}
                        {sending ? t('invoices.sending') : t('invoices.send_cta')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
